import { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { getRealtime, disconnectRealtime, NEW_NOTIFICATION_EVENT } from './client';
import { baseRealtimeHandlers } from './registry';

/**
 * Shared listener shell for realtime user notifications (pixel-realtime / Reverb).
 *
 * One subscription to the signed-in user's private notifications channel, fanned
 * out by notification `type` to the provided `handlers` map. Each app passes its
 * own merged registry (base + app-specific); defaults to the base handlers.
 *
 * Renders nothing; mount once inside the authenticated app shell.
 * Realtime is best-effort — a socket failure never breaks the app.
 *
 * @param {object} props
 * @param {Object.<string, Function>} [props.handlers] type → handler(data, { dispatch }).
 */
const RealtimeNotificationsListener = ({ handlers = baseRealtimeHandlers }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state?.loginUser?.user?.id, shallowEqual);
  const isLogin = useSelector((state) => state?.loginUser?.isLogin, shallowEqual);

  useEffect(() => {
    if (!isLogin || !userId) {
      disconnectRealtime();
      return undefined;
    }

    let subscription;

    const handleNotification = (notification) => {
      // The navbar bell listens for this instead of re-fetching; the payload is the full stored row.
      window.dispatchEvent(new CustomEvent(NEW_NOTIFICATION_EVENT, { detail: notification }));

      const handler = handlers[notification?.type];
      if (handler) {
        handler(notification?.data || {}, { dispatch });
      }
    };

    try {
      subscription = getRealtime().notifications.listen(handleNotification, userId);
    } catch (error) {
      // Best-effort: never break the app if the realtime socket can't connect.
      // eslint-disable-next-line no-console
      console.warn('Realtime notifications listener failed to subscribe:', error);
    }

    return () => {
      try {
        subscription?.stop?.();
      } catch (error) {
        /* no-op */
      }
    };
  }, [isLogin, userId, dispatch, handlers]);

  return null;
};

export default RealtimeNotificationsListener;
